import { langFromPath, LANG_PREFIX } from "./seo";
import { normalizeNoDepositText } from "./i18n";
import exoticCategoryImage from "../assets/category-images/exotic.webp";
import luxuryCategoryImage from "../assets/category-images/luxury.webp";
import suvCategoryImage from "../assets/category-images/suv.webp";
import convertibleCategoryImage from "../assets/category-images/convertible.webp";
import monthlyCategoryImage from "../assets/category-images/monthly.webp";

// Category (Rotana-style) SEO landing pages. Cars are matched by rules so new
// vehicles are automatically placed in the right category.

const has = (v, list) => list.includes((v || "").trim());
const nameHas = (car, words) => {
  const s = `${car.name || ""} ${car.model || ""}`.toLowerCase();
  return words.some((w) => s.includes(w));
};

const hasSupplierCategory = (car, labels) => {
  const categories = car.supplier_categories || [];
  return labels.some((label) => categories.includes(label));
};

const hasPublicCategory = (car, key) => (
  (car.category_keys || []).includes(key)
);

const matchesPublicCategory = (car, key, legacyMatch) => {
  if (Array.isArray(car.category_keys) && car.category_keys.length > 0) {
    return hasPublicCategory(car, key);
  }

  return legacyMatch();
};

const SUV_KEYWORDS = [
  "urus", "cullinan", "bentayga", "g63", "g 63", "dbx", "purosangue", "escalade",
  "patrol", "land cruiser", "cayenne", "levante", "grecale", "x5", "x6", "x7", "xm",
  "q8", "rs q8", "gle", "gls", " rx", " lx", "defender", "discovery", "velar",
  "evoque", "tahoe", "suburban", "range rover",
];

const rawCategories = [
  {
    key: "exotic",
    slug: "exotic-car-rental-dubai",
    homeRow: true,
    homeCategoryCard: true,
    keywords: "exotic car rental dubai, rent exotic car dubai, exotic cars for hire dubai",
    image: exoticCategoryImage,
    en: {
      name: "Exotic Cars",
      h1: "Exotic Car Rental in Dubai",
      sub: "Ferrari, Lamborghini, McLaren",
      intro: [
        "Rent an exotic car in Dubai and command every road from Sheikh Zayed Road to the Palm. Silver Drive's exotic fleet gathers the most sought-after supercars on earth — Ferrari, Lamborghini, McLaren, Aston Martin and more — each hand-detailed and delivered free to your hotel or residence, 24/7.",
        "Every exotic rental includes comprehensive insurance, transparent daily rates and no cash deposit — only a fully refundable pre-authorisation. Whether it's a weekend blast, a wedding entrance or a photoshoot on Dubai Marina, our concierge tailors the experience end to end.",
      ],
    },
    tr: {
      name: "Egzotik Araçlar",
      h1: "Dubai'de Egzotik Araç Kiralama",
      sub: "Ferrari, Lamborghini, McLaren",
      intro: [
        "Dubai'de egzotik bir araç kiralayın ve Sheikh Zayed Yolu'ndan Palm'a kadar her yola hükmedin. Silver Drive'ın egzotik filosu dünyanın en çok aranan süper otomobillerini bir araya getirir — Ferrari, Lamborghini, McLaren, Aston Martin ve daha fazlası — her biri özenle detaylandırılmış ve otelinize veya rezidansınıza 7/24 ücretsiz teslim edilir.",
        "Her egzotik kiralamaya kapsamlı sigorta, şeffaf günlük fiyatlar ve nakit depozito olmadan yalnızca tamamen iade edilebilir bir ön provizyon dahildir. İster hafta sonu kaçamağı, ister düğün girişi, ister Dubai Marina'da bir çekim olsun, concierge ekibimiz deneyimi baştan sona size özel hazırlar.",
      ],
    },
    ru: {
      name: "Экзотические авто",
      h1: "Аренда экзотических авто в Дубае",
      sub: "Ferrari, Lamborghini, McLaren",
      intro: [
        "Арендуйте экзотический автомобиль в Дубае и владейте каждой дорогой — от Sheikh Zayed Road до Palm. Экзотический автопарк Silver Drive собирает самые желанные суперкары мира — Ferrari, Lamborghini, McLaren, Aston Martin и другие — каждый вручную детейлится и бесплатно доставляется в ваш отель или резиденцию 24/7.",
        "Каждая аренда экзотики включает полную страховку, прозрачные суточные цены и отсутствие наличного депозита — только полностью возвратную преавторизацию. Будь то выходные, свадебный выезд или фотосессия на Dubai Marina — наш консьерж создаёт опыт под ключ.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "exotic", () => (
      has(car.brand, ["Ferrari", "Lamborghini", "McLaren", "Aston Martin", "Maserati", "Bugatti", "Koenigsegg", "Pagani"]) ||
      car.category === "Supercar" ||
      hasSupplierCategory(car, ["Exotic Car Rental"])
    )),
  },
  {
    key: "sports",
    slug: "sports-car-rental-dubai",
    homeRow: true,
    keywords: "sports car rental dubai, rent sports car dubai, hire sports car dubai",
    image: (
      "/api/files/silverdrive/rotana-corrected/" +
      "045-yellow-chevrolet-corvette-z06-2025/01.webp"
    ),
    en: {
      name: "Sports Cars",
      h1: "Sports Car Rental in Dubai",
      sub: "Porsche, Corvette, Mustang",
      intro: [
        "Rent a sports car in Dubai for pure driving thrills without the six-figure price tag. From flat-six Porsches to muscular grand tourers and open-top convertibles, Silver Drive's sports selection is built for the enthusiast who wants agility, sound and style in equal measure.",
        "All sports car rentals come with full insurance, flexible daily-to-monthly terms and complimentary delivery across the Emirates. No deposit, no hidden charges — just the keys and the open road.",
      ],
    },
    tr: {
      name: "Spor Araçlar",
      h1: "Dubai'de Spor Araç Kiralama",
      sub: "Porsche, Corvette, Mustang",
      intro: [
        "Dubai'de altı haneli fiyat etiketi olmadan saf sürüş heyecanı için bir spor araç kiralayın. Boxer motorlu Porsche'lerden kaslı grand tourer'lara ve üstü açık cabriolara kadar Silver Drive'ın spor seçkisi; çeviklik, ses ve stili eşit ölçüde isteyen tutkunlar için tasarlandı.",
        "Tüm spor araç kiralamalarına kapsamlı sigorta, esnek günlükten aylığa şartlar ve BAE geneli ücretsiz teslimat dahildir. Depozito yok, gizli ücret yok — sadece anahtar ve açık yol.",
      ],
    },
    ru: {
      name: "Спорткары",
      h1: "Аренда спорткаров в Дубае",
      sub: "Porsche, Corvette, Mustang",
      intro: [
        "Арендуйте спорткар в Дубае ради чистого драйва без шестизначного ценника. От оппозитных Porsche до мускулистых гран-туреров и открытых кабриолетов — спортивная подборка Silver Drive создана для энтузиастов, которым в равной мере важны управляемость, звук и стиль.",
        "Все аренды спорткаров включают полную страховку, гибкие условия от суток до месяца и бесплатную доставку по всем Эмиратам. Без депозита и скрытых платежей — только ключи и открытая дорога.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "sports", () => (
      ["Supercar", "Grand Tourer", "Convertible"].includes(car.category) ||
      has(car.brand, ["Porsche", "Chevrolet", "Ford", "Nissan"]) ||
      nameHas(car, ["gt-r", "gtr", "corvette", "mustang", "supra", "911", "718", "m4", "m8", "amg gt"]) ||
      hasSupplierCategory(car, ["Sports Car Rental"])
    )),
  },
  {
    key: "luxury",
    slug: "luxury-car-rental-dubai",
    homeRow: true,
    homeCategoryCard: true,
    keywords: "luxury car rental dubai, rent luxury car dubai, chauffeur car hire dubai",
    image: luxuryCategoryImage,
    en: {
      name: "Luxury Cars",
      h1: "Luxury Car Rental in Dubai",
      sub: "Rolls-Royce, Bentley, Maybach",
      intro: [
        "Rent a luxury car in Dubai for effortless prestige and first-class comfort. Silver Drive's luxury line-up is led by Rolls-Royce, Bentley, Mercedes-Maybach and the flagship saloons — ideal for weddings, business arrivals at DIFC or serene cruises through Downtown.",
        "Self-drive or chauffeur, every luxury rental includes comprehensive insurance and a refundable pre-authorisation instead of a cash deposit. Free delivery across Dubai and the Emirates, around the clock.",
      ],
    },
    tr: {
      name: "Lüks Araçlar",
      h1: "Dubai'de Lüks Araç Kiralama",
      sub: "Rolls-Royce, Bentley, Maybach",
      intro: [
        "Dubai'de zahmetsiz prestij ve birinci sınıf konfor için lüks bir araç kiralayın. Silver Drive'ın lüks serisine Rolls-Royce, Bentley, Mercedes-Maybach ve amiral gemisi sedanlar öncülük eder — düğünler, DIFC'de iş girişleri veya Downtown'da huzurlu turlar için idealdir.",
        "İster şoförlü ister kendiniz sürün, her lüks kiralamaya kapsamlı sigorta ve nakit depozito yerine iade edilebilir ön provizyon dahildir. Dubai ve Emirlikler geneli 7/24 ücretsiz teslimat.",
      ],
    },
    ru: {
      name: "Люкс авто",
      h1: "Аренда люкс-авто в Дубае",
      sub: "Rolls-Royce, Bentley, Maybach",
      intro: [
        "Арендуйте люксовый автомобиль в Дубае ради непринуждённого престижа и первоклассного комфорта. Люкс-линейку Silver Drive возглавляют Rolls-Royce, Bentley, Mercedes-Maybach и флагманские седаны — идеально для свадеб, деловых прибытий в DIFC или неспешных поездок по Downtown.",
        "С водителем или без — каждая люкс-аренда включает полную страховку и возвратную преавторизацию вместо наличного депозита. Бесплатная доставка по Дубаю и Эмиратам, круглосуточно.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "luxury", () => (
      has(car.brand, ["Rolls-Royce", "Bentley", "Maybach", "Lexus", "Cadillac"]) ||
      car.category === "Sedan" ||
      nameHas(car, ["maybach", "s580", "s500", "s-class", "s 500", "flying spur", "ghost", "phantom", "7 series", "i7", "740"]) ||
      hasSupplierCategory(car, ["Luxury Car Rental"])
    )),
  },
  {
    key: "suv",
    slug: "suv-rental-dubai",
    homeRow: true,
    homeCategoryCard: true,
    keywords: "suv rental dubai, luxury suv rental dubai, rent range rover dubai, rent g63 dubai",
    image: suvCategoryImage,
    en: {
      name: "Luxury SUVs",
      h1: "Luxury SUV Rental in Dubai",
      sub: "Range Rover, Urus, G63",
      intro: [
        "Rent a luxury SUV in Dubai for commanding presence and go-anywhere versatility — equally at home on a hotel forecourt or a desert track. From the Range Rover and Mercedes G63 to the Lamborghini Urus and Rolls-Royce Cullinan, Silver Drive covers the full spectrum of premium SUVs.",
        "The default choice for families, groups and desert escapes. Every SUV rental includes comprehensive insurance, free UAE-wide delivery and flexible monthly rates — with no cash deposit required.",
      ],
    },
    tr: {
      name: "Lüks SUV'lar",
      h1: "Dubai'de Lüks SUV Kiralama",
      sub: "Range Rover, Urus, G63",
      intro: [
        "Dubai'de heybetli duruş ve her yere gidebilen çok yönlülük için lüks bir SUV kiralayın — bir otel önünde de bir çöl pistinde de kendini evinde hisseder. Range Rover ve Mercedes G63'ten Lamborghini Urus ve Rolls-Royce Cullinan'a kadar Silver Drive premium SUV yelpazesinin tamamını kapsar.",
        "Aileler, gruplar ve çöl kaçamakları için varsayılan tercih. Her SUV kiralamaya kapsamlı sigorta, BAE geneli ücretsiz teslimat ve esnek aylık fiyatlar dahildir — nakit depozito gerekmez.",
      ],
    },
    ru: {
      name: "Люкс внедорожники",
      h1: "Аренда люкс-внедорожников в Дубае",
      sub: "Range Rover, Urus, G63",
      intro: [
        "Арендуйте люксовый внедорожник в Дубае ради внушительного присутствия и вседорожной универсальности — одинаково уместен и у отеля, и на пустынной трассе. От Range Rover и Mercedes G63 до Lamborghini Urus и Rolls-Royce Cullinan — Silver Drive охватывает весь спектр премиальных SUV.",
        "Выбор по умолчанию для семей, компаний и поездок в пустыню. Каждая аренда SUV включает полную страховку, бесплатную доставку по ОАЭ и гибкие месячные тарифы — без наличного депозита.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "suv", () => (
      car.category === "Luxury SUV" ||
      has(car.brand, ["Range Rover"]) ||
      nameHas(car, SUV_KEYWORDS) ||
      hasSupplierCategory(car, [
        "7-Seater SUV Rental",
        "Luxury SUV Rental",
        "Sports SUV Rental",
      ])
    )),
  },
  {
    key: "seven-seater-suv",
    slug: "7-seater-suv-rental-dubai",
    showOnHomeFleet: false,
    keywords: "7 seater suv rental dubai, rent cadillac escalade dubai, family suv rental dubai",
    image: "/api/files/silverdrive/rotana-corrected/008-black-cadillac-escalade-2025/01.webp",
    en: {
      name: "7-Seater SUV Rental",
      h1: "7-Seater SUV Rental in Dubai",
      sub: "Cadillac Escalade for family and VIP travel",
      intro: [
        "Rent a spacious 7-seater SUV in Dubai for family trips, airport transfers and VIP groups. " +
          "Silver Drive pairs generous seating with refined comfort and delivery across the city.",
        "Enjoy a premium cabin, flexible rental terms and concierge support from reservation to " +
          "collection. Every booking includes transparent pricing and complimentary delivery.",
      ],
    },
    tr: {
      name: "7 Koltuklu SUV Kiralama",
      h1: "Dubai'de 7 Koltuklu SUV Kiralama",
      sub: "Aileler ve VIP transferler için Cadillac Escalade",
      intro: [
        "Aile gezileri, havalimanı transferleri ve VIP gruplar için Dubai'de geniş 7 koltuklu SUV " +
          "kiralayın. Silver Drive, üst düzey konforu şehir genelinde teslimatla birleştirir.",
        "Geniş kabin, esnek kiralama süreleri ve rezervasyondan teslimata concierge desteğinin " +
          "keyfini çıkarın. Her rezervasyonda şeffaf fiyatlandırma ve ücretsiz teslimat sunulur.",
      ],
    },
    ru: {
      name: "Аренда 7-местного SUV",
      h1: "Аренда 7-местного SUV в Дубае",
      sub: "Cadillac Escalade для семьи и VIP-поездок",
      intro: [
        "Арендуйте просторный 7-местный SUV в Дубае для семейных поездок, трансферов и VIP-групп. " +
          "Silver Drive сочетает высокий комфорт с доставкой по всему городу.",
        "Вас ждут просторный салон, гибкие сроки аренды и поддержка консьержа от бронирования до " +
          "возврата. В каждую аренду входят прозрачные цены и бесплатная доставка.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "seven-seater-suv", () => (
      hasSupplierCategory(car, ["7-Seater SUV Rental"])
    )),
  },
  {
    key: "convertible",
    slug: "convertible-car-rental-dubai",
    homeCategoryCard: true,
    keywords: "convertible rental dubai, cabrio car hire dubai, luxury convertible rental dubai",
    image: convertibleCategoryImage,
    en: {
      name: "Convertible Rental",
      h1: "Convertible Car Rental in Dubai",
      sub: "Open-top driving with Porsche, Bentley and Ferrari",
      intro: [
        "Rent a convertible in Dubai for open-air drives along the coast, Palm Jumeirah and the " +
          "city's most iconic boulevards. Choose from performance cabriolets and refined grand tourers.",
        "Every convertible is prepared for an effortless arrival, with flexible daily rates, " +
          "full insurance and complimentary delivery across Dubai.",
      ],
    },
    tr: {
      name: "Cabrio Araç Kiralama",
      h1: "Dubai'de Cabrio Araç Kiralama",
      sub: "Porsche, Bentley ve Ferrari ile üstü açık sürüş",
      intro: [
        "Sahil yolu, Palm Jumeirah ve şehrin ikonik bulvarlarında üstü açık sürüş için Dubai'de " +
          "cabrio araç kiralayın. Performans cabrioları ve seçkin grand tourer modelleri arasından seçim yapın.",
        "Her cabrio; esnek günlük fiyatlar, kapsamlı sigorta ve Dubai geneli ücretsiz teslimatla " +
          "kusursuz bir deneyim için hazırlanır.",
      ],
    },
    ru: {
      name: "Аренда кабриолета",
      h1: "Аренда кабриолета в Дубае",
      sub: "Открытый верх: Porsche, Bentley и Ferrari",
      intro: [
        "Арендуйте кабриолет в Дубае для поездок с открытым верхом по побережью, Palm Jumeirah и " +
          "знаковым бульварам города. Выбирайте динамичные кабриолеты и утончённые гран-туреры.",
        "Каждый кабриолет подготовлен к безупречной поездке с гибкими тарифами, полной страховкой " +
          "и бесплатной доставкой по Дубаю.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "convertible", () => (
      car.category === "Convertible" ||
      hasSupplierCategory(car, ["Convertible Rental"])
    )),
  },
  {
    key: "monthly",
    slug: "monthly-car-rental-dubai",
    homeCategoryCard: true,
    keywords: "monthly car rental dubai, long term luxury car rental dubai, monthly suv rental dubai",
    image: monthlyCategoryImage,
    en: {
      name: "Monthly Car Rental",
      h1: "Monthly Car Rental in Dubai",
      sub: "Flexible long-term luxury and performance rentals",
      intro: [
        "Choose a monthly car rental in Dubai when you want the freedom of a premium vehicle for " +
          "longer stays, business schedules or extended holidays.",
        "Silver Drive offers competitive monthly terms, concierge delivery and a curated choice of " +
          "luxury SUVs, sports cars and executive models.",
      ],
    },
    tr: {
      name: "Aylık Araç Kiralama",
      h1: "Dubai'de Aylık Araç Kiralama",
      sub: "Uzun dönem lüks ve performans araç kiralama",
      intro: [
        "Uzun konaklamalar, iş programları veya geniş tatiller için premium bir araca sahip olma " +
          "özgürlüğünü Dubai'de aylık araç kiralama ile yaşayın.",
        "Silver Drive; rekabetçi aylık koşullar, concierge teslimatı ve lüks SUV, spor araç ile " +
          "executive modellerden oluşan seçkin bir yelpaze sunar.",
      ],
    },
    ru: {
      name: "Месячная аренда авто",
      h1: "Месячная аренда авто в Дубае",
      sub: "Гибкая долгосрочная аренда люкс- и спорткаров",
      intro: [
        "Выберите месячную аренду авто в Дубае, чтобы пользоваться премиальным автомобилем во " +
          "время длительного проживания, делового графика или продолжительного отпуска.",
        "Silver Drive предлагает выгодные месячные условия, доставку консьержем и широкий выбор " +
          "люксовых SUV, спорткаров и представительских моделей.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "monthly", () => (
      hasSupplierCategory(car, ["Monthly Car Rental"])
    )),
  },
  {
    key: "sports-suv",
    slug: "sports-suv-rental-dubai",
    keywords: "sports suv rental dubai, lamborghini urus rental dubai, performance suv hire dubai",
    image: "/api/files/silverdrive/rotana-corrected/023-green-lamborghini-urus-se-2025/01.webp",
    en: {
      name: "Sports SUV Rental",
      h1: "Sports SUV Rental in Dubai",
      sub: "Lamborghini Urus, G63 and performance SUVs",
      intro: [
        "Rent a sports SUV in Dubai for supercar energy with everyday space and confidence. Our " +
          "selection combines bold design, rapid performance and premium comfort.",
        "From Lamborghini Urus to high-performance G-Class models, every sports SUV is available " +
          "with flexible terms, insurance and complimentary delivery.",
      ],
    },
    tr: {
      name: "Spor SUV Kiralama",
      h1: "Dubai'de Spor SUV Kiralama",
      sub: "Lamborghini Urus, G63 ve performans SUV'lar",
      intro: [
        "Günlük kullanışlılığı ve güçlü karakteri birleştiren süper otomobil enerjisi için Dubai'de " +
          "spor SUV kiralayın. Seçkimiz cesur tasarımı, hızlı performansı ve premium konforu bir araya getirir.",
        "Lamborghini Urus'tan yüksek performanslı G-Class modellere kadar her spor SUV; esnek " +
          "süreler, sigorta ve ücretsiz teslimatla sunulur.",
      ],
    },
    ru: {
      name: "Аренда спортивного SUV",
      h1: "Аренда спортивного SUV в Дубае",
      sub: "Lamborghini Urus, G63 и динамичные SUV",
      intro: [
        "Арендуйте спортивный SUV в Дубае, чтобы получить энергию суперкара вместе с ежедневным " +
          "простором и уверенностью. Наша подборка объединяет яркий дизайн, динамику и премиальный комфорт.",
        "От Lamborghini Urus до высокопроизводительных G-Class — каждый спортивный SUV доступен " +
          "на гибких условиях, со страховкой и бесплатной доставкой.",
      ],
    },
    match: (car) => matchesPublicCategory(car, "sports-suv", () => (
      hasSupplierCategory(car, ["Sports SUV Rental"])
    )),
  },
];

export const CATEGORIES = normalizeNoDepositText(rawCategories);

export const CATEGORY_SLUGS = CATEGORIES.map((c) => c.slug);
const CAT_L10N_SLUG = {
  exotic: { tr: "egzotik-arac-kiralama-dubai", ru: "аренда-экзотических-авто-дубай" },
  sports: { tr: "spor-arac-kiralama-dubai", ru: "аренда-спорткаров-дубай" },
  luxury: { tr: "luks-arac-kiralama-dubai", ru: "аренда-люкс-авто-дубай" },
  suv: { tr: "suv-kiralama-dubai", ru: "аренда-внедорожников-дубай" },
  "seven-seater-suv": {
    tr: "7-kisilik-suv-kiralama-dubai",
    ru: "аренда-7-местных-внедорожников-дубай",
  },
  convertible: {
    tr: "cabrio-arac-kiralama-dubai",
    ru: "аренда-кабриолетов-дубай",
  },
  monthly: {
    tr: "aylik-arac-kiralama-dubai",
    ru: "месячная-аренда-авто-дубай",
  },
  "sports-suv": {
    tr: "spor-suv-kiralama-dubai",
    ru: "аренда-спортивных-внедорожников-дубай",
  },
};
export const catSlugFor = (cat, lang = "EN") => {
  const localized = CAT_L10N_SLUG[cat.key];

  if (lang === "TR") {
    return localized?.tr || cat.slug;
  }

  if (lang === "RU") {
    return localized?.ru || cat.slug;
  }

  return cat.slug;
};
export const catPathFor = (
  cat,
  lang = langFromPath(typeof window !== "undefined" ? window.location.pathname : "/"),
) => {
  const l = lang;
  return `${LANG_PREFIX[l] || ""}/rent/${catSlugFor(cat, l)}`;
};
export const categoryBySlug = (slug) => (
  CATEGORIES.find((category) => {
    const localized = CAT_L10N_SLUG[category.key];
    return (
      category.slug === slug ||
      localized?.tr === slug ||
      localized?.ru === slug
    );
  }) || null
);
export const catL10n = (cat, lang) => cat[String(lang || "EN").toLowerCase()] || cat.en;
